import {observer} from 'mobx-react-lite'
import React from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {StickerIcon} from 'yougile-model'
import {Backend} from '@common/backend'
import {LinkButton} from '@components/common/base/buttons'
import {Icon} from '@components/common/base/icon'
import {InputNew} from '@components/common/base/input-new'
import {Select} from '@components/common/base/select'
import {BgOpacityContainer} from '@components/common/bg-opacity-container/bg-opacity-container'
import {DropdownContent} from '@components/common/dropdown/dropdown-content'
import {
  CustomIcons,
  stickerIcons
} from '@components/common/icon-tabler/custom-icons-16'
import {useBoolean} from '@containers/hooks'
import {useStickerEditorStore} from './sticker-editor-store'

export const StickerName = observer(() => {
  const [state, store] = useStickerEditorStore()
  const visible = useBoolean(false)

  const onSelect = (icon: StickerIcon): void => {
    store.setStickerChanged(true)
    store.setStickerIcon(icon)
    visible.turnOff()
  }
  const setStickerName = (name: string): void => {
    store.setStickerChanged(true)
    store.setStickerName(name)
  }
  const iconDropdownAvailable = ['string', 'text', 'numeric'].includes(
    state.stickerType
  )

  return (
    <div className="flex items-center gap-8">
      {iconDropdownAvailable && (
        <Select
          variant="new"
          className="!w-fit"
          truncate={false}
          open={visible.value}
          onOpenChange={visible.setValue}
          content={
            <DropdownContent>
              <IconsContainer onSelect={onSelect} current={state.stickerIcon} />
              <div className="px-8">
                <LinkButton
                  icon="IconCircleX"
                  onClick={() => onSelect('')}
                  className="pt-6 pb-8"
                >
                  {Lang.msg.myStickers.withoutIcon}
                </LinkButton>
              </div>
            </DropdownContent>
          }
        >
          {state.stickerIcon ? (
            <Icon icon={state.stickerIcon} />
          ) : (
            <Icon color="placeholder" icon="IconCustomBan" />
          )}
        </Select>
      )}
      <InputNew
        className="w-full"
        autoFocus
        value={state.stickerName}
        onChange={setStickerName}
        placeholder={Lang.msg.myStickers.namePlaceholder}
        error={store.fieldHasError('name')}
      />
    </div>
  )
})

const TwIconsContainer = tw.div`
  flex
  flex-wrap
  gap-8
  p-8
`

const TwIconWrapper = tw.div`
  cursor-pointer
  p-2
`

const IconsContainer = ({
  onSelect,
  current
}: {
  onSelect: (icon: StickerIcon) => void
  current?: CustomIcons | ''
}): React.ReactElement => {
  const stickerIconsKeys = (
    Backend.features.isLastVersionIconsFeasible
      ? Object.keys(stickerIcons)
      : Object.keys(stickerIcons).filter(
          iconKey => !['ruble', 'euro'].includes(iconKey)
        )
  ) as StickerIcon[]

  return (
    <TwIconsContainer style={{maxWidth: 288}}>
      {stickerIconsKeys.map(icon => {
        const isCurrentIcon = icon === current
        return (
          <div className="relative w-20 h-20" key={icon}>
            <BgOpacityContainer
              className="w-28 h-28"
              bgColorClassName="bg-action-default rounded-full"
              bgOpacityClassName={isCurrentIcon ? 'opacity-20' : 'opacity-0'}
              bgPosition={isCurrentIcon ? '-top-4 -left-4' : ''}
            >
              <TwIconWrapper onClick={() => onSelect(icon)}>
                {icon && <Icon icon={icon} />}
              </TwIconWrapper>
            </BgOpacityContainer>
          </div>
        )
      })}
    </TwIconsContainer>
  )
}
